import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToSection, pathForSection } from "@/lib/scrollToSection";
import { Menu, X, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SocialIcons from "@/components/SocialIcons";
import { useSiteImage } from "@/hooks/useSiteImage";
import { useSiteSetting } from "@/hooks/useSiteSetting";
import { useActivitiesEnabled } from "@/hooks/useActivitiesEnabled";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const logoUrl = useSiteImage("logo", "");
  const logoHeight = Math.max(20, Math.min(120, Number(useSiteSetting("ui.logo_height_px", "40")) || 40));
  const activitiesEnabled = useActivitiesEnabled();
  const navigate = useNavigate();
  const isHome = location.pathname === "/" || location.pathname === "/home";

  const navLinks = [
    { href: "#", label: t("nav.home", "Home") },
    { href: "#services", label: t("nav.services") },
    { href: "#fleet", label: t("nav.fleet") },
    { to: "/tarifs", label: t("nav.pricing") },
    ...(activitiesEnabled ? [{ href: "#activities", label: t("nav.activities") }] : []),
    { href: "#destinations", label: t("nav.excursions") },
    { to: "/guide", label: t("nav.guide") },
    { href: "#booking", label: t("nav.booking") },
  ];

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsOpen(false);
    navigate(pathForSection(href));
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl border-b border-border shadow-sm">
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <a href="/home" onClick={(e) => handleAnchorClick(e, "#")} className="flex items-center gap-2 font-heading text-xl md:text-2xl font-bold text-primary tracking-tight">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={t("nav.logoAlt")}
              width={logoHeight * 3}
              height={logoHeight}
              loading="eager"
              decoding="async"
              className="w-auto object-contain"
              style={{ height: `${logoHeight}px` }}
            />
          ) : (
            <span>maroctransfers.com</span>
          )}
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            "to" in link ? (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "text-sm font-body tracking-wide transition-colors",
                  location.pathname === link.to ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary"
                )}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={pathForSection(link.href)}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className="text-sm font-body tracking-wide text-muted-foreground hover:text-primary transition-colors"
              >
                {link.label}
              </a>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <SocialIcons className="gap-1" />
          <LanguageSwitcher />
          <a href="tel:+212706280215" className="flex items-center gap-2 text-sm text-primary font-body font-semibold">
            <Phone className="w-4 h-4" />
            +212 706 280 215
          </a>
          <Button asChild className="bg-primary text-primary-foreground font-body font-semibold rounded-full px-6 hover:bg-primary/90 shadow-md transition-all">
            <a href="/reservation" onClick={(e) => handleAnchorClick(e, "#booking")}>{t("nav.book")}</a>
          </Button>
        </div>

        <div className="flex lg:hidden items-center gap-3">
          <LanguageSwitcher />
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-foreground"
            aria-label={isOpen ? t("nav.menuClose") : t("nav.menuOpen")}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden bg-card border-t border-border px-6 py-6 space-y-4">
          {navLinks.map((link) =>
            "to" in link ? (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "block transition-colors font-body",
                  location.pathname === link.to ? "text-primary font-semibold" : "text-foreground hover:text-primary"
                )}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={pathForSection(link.href)}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className="block text-foreground hover:text-primary transition-colors font-body"
              >
                {link.label}
              </a>
            )
          )}
          <SocialIcons className="justify-center gap-3 pt-2" />
          <Button asChild className="w-full bg-primary text-primary-foreground font-body font-semibold rounded-full">
            <a href="/reservation" onClick={(e) => handleAnchorClick(e, "#booking")}>{t("nav.bookNow")}</a>
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
